import React, { useEffect, useState } from "react";
import {
    Card, Typography, Form, Input, Select, DatePicker, Button,
    Row, Col, Space, InputNumber, message, Tag,
} from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { MilestoneService } from "../../../Services/Manage/MilestoneService";
import { UserService } from "../../../Services/Manage/UserService";
import { DepartmentEmpServices } from "../../../Services/Manage/DepartmentEmpServices";

const { TextArea } = Input;
const { Title, Text } = Typography;

const compactFormStyle = `
  .ant-form-item { margin-bottom: 15px !important; }
  .ant-card-body { padding: 16px 16px !important; }
  .ant-card { margin-bottom: 16px !important; }
  .ant-form-item-label { padding-bottom: 2px !important; }
  .ant-form-item-label > label { height: 20px !important; }
`;

// Exact same CardTitle pattern as AddRequirement.jsx — no icon, "no. text" format
const CardTitle = ({ no, text }) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "rgba(14, 93, 146, 0.95)" }}>
        <span style={{ fontWeight: 600 }}>{no}. {text}</span>
    </span>
);

const priorityOptions = [
    { value: "High", label: "High" },
    { value: "Medium", label: "Medium" },
    { value: "Low", label: "Low" },
];

const statusOptions = [
    { value: "Planned", label: "Planned" },
    { value: "In Progress", label: "In Progress" },
    { value: "Pending", label: "Pending" },
    { value: "Completed", label: "Completed" },
    { value: "Overdue", label: "Overdue" },
];

const effortOptions = [
    { value: "Small (1-2 days)", label: "Small (1-2 days)" },
    { value: "Medium (3-5 days)", label: "Medium (3-5 days)" },
    { value: "Large (1-2 weeks)", label: "Large (1-2 weeks)" },
    { value: "X-Large (2+ weeks)", label: "X-Large (2+ weeks)" },
];

const AddMilestone = () => {
    const [messageApi, contextHolder] = message.useMessage();
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const location = useLocation();

    const [milestoneTypeOptions, setMilestoneTypeOptions] = useState([]);
    const [assigneeOptions, setAssigneeOptions] = useState([]);
    const [dependencyOptions, setDependencyOptions] = useState([]);

    const projectId = location.state?.id || null;
    const projectName = location.state?.pro_name || "-";
    const projectNo = location.state?.pro_no || "-";
    const projectDepartment = location.state?.pro_department_name || "-";

    // Edit / View mode support
    // Edit / View mode support
    const milestoneDbId = location.state?.milestoneDbId || null;
    const enableEdit = location.state?.enableEdit ?? true;
    const isEditMode = !!milestoneDbId;
    const readOnly = isEditMode && !enableEdit;
    const statusOnly = location.state?.statusOnly === true;
    const canManage = location.state?.canManage === true;

    const [milestoneCode, setMilestoneCode] = useState("MS-001");
    const [existingRecord, setExistingRecord] = useState(null);
    const [loadingRecord, setLoadingRecord] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);

    const summary = Form.useWatch([], form) || {};

    const startDate = Form.useWatch("start_date", form);
    const endDate = Form.useWatch("end_date", form);
    const durationDays = (startDate && endDate)
        ? Math.max(dayjs(endDate).diff(dayjs(startDate), "day"), 0)
        : 0;

    useEffect(() => {
        if (isEditMode) {
            getMilestoneById(milestoneDbId);
        } else {
            getLastMilestone();
        }
    }, [milestoneDbId]);

    useEffect(() => {
        getAssigneeOptions();
        getDependencyOptions();
        getDropdownValues("milestone_type", setMilestoneTypeOptions);
    }, []);

    const getDropdownValues = async (field, setter) => {
        try {
            const request = await MilestoneService.getDropdownValues(field);
            if (request?.status) {
                setter((request.data || []).map((v) => ({ value: v, label: v })));
            }
        } catch (error) {
            console.error(error);
        }
    };

    // const getDepartmentOptions = async () => {
    //     try {
    //         const request = await DepartmentEmpServices.getAllDepartment();
    //         const option = (request.data || []).map((item) => ({ value: String(item.id), label: item.name }));
    //         setDepartmentOptions(option);
    //     } catch (error) {
    //         console.error(error);
    //     }
    // };

    const getAssigneeOptions = async () => {
        try {
            let engineerIds = location.state?.pro_engineer;
            if (typeof engineerIds === "string") {
                try { engineerIds = JSON.parse(engineerIds); } catch { engineerIds = []; }
            }
            const allowedIds = new Set((engineerIds || []).filter(Boolean).map((id) => String(id).trim()));

            const request = await UserService.getAllUsers();
            const users = request?.data || [];
            const relevant = allowedIds.size
                ? users.filter((u) => allowedIds.has(String(u.user_id).trim()))
                : users;

            setAssigneeOptions(
                relevant.map((u) => ({
                    value: u.name,
                    label: `${u.name}${u.user_id ? ` (${u.user_id})` : ""}`,
                }))
            );
        } catch (error) {
            console.error(error);
        }
    };

    const getDependencyOptions = async () => {
        try {
            const request = projectId
                ? await MilestoneService.getAllByProject(projectId)
                : await MilestoneService.getAllMilestone();

            if (request?.status) {
                const rows = (request.data || [])
                    .filter((m) => m.id !== milestoneDbId)
                    .map((m) => ({
                        value: m.milestone_code,
                        label: `${m.milestone_code} - ${m.milestone_name}`,
                    }));
                setDependencyOptions(rows);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const getLastMilestone = async () => {
        try {
            const request = await MilestoneService.getLastMilestone();
            let nextNo = "001";
            if (request?.status && request?.data?.milestone_code) {
                const seqPart = request.data.milestone_code.split("-").pop();
                const parsed = parseInt(seqPart, 10);
                if (!isNaN(parsed)) {
                    nextNo = String(parsed + 1).padStart(3, "0");
                }
            }
            setMilestoneCode(`MS-${nextNo}`);
        } catch (error) {
            console.error(error);
        }
    };

    const getMilestoneById = async (id) => {
        try {
            setLoadingRecord(true);
            const request = await MilestoneService.getMilestoneById(id);
            if (request?.status && request?.data) {
                const data = request.data;
                setExistingRecord(data);
                setMilestoneCode(data.milestone_code || "MS-001");

                form.setFieldsValue({
                    milestone_name: data.milestone_name,
                    description: data.description,
                    start_date: data.start_date ? dayjs(data.start_date) : null,
                    end_date: data.end_date ? dayjs(data.end_date) : null,
                    due_date: data.due_date ? dayjs(data.due_date) : null,
                    assignee: data.assignee,
                    department: data.department,
                    priority: data.priority,
                    progress: data.progress,
                    milestone_type: data.milestone_type ? [data.milestone_type] : [],
                    dependency: data.dependency,
                    related_milestone: data.related_milestone,
                    status: data.status,
                    estimated_effort: data.estimated_effort,
                    estimated_cost: data.estimated_cost,
                    deliverables: data.deliverables,
                    remarks: data.remarks,
                });
            } else {
                messageApi.error(request?.message || "Milestone not found");
            }
        } catch (error) {
            console.error("Error fetching milestone:", error);
            messageApi.error("Failed to load milestone");
        } finally {
            setLoadingRecord(false);
        }
    };

    const buildPayload = async (values, isDraft) => {
        const user = await UserService.getOmeUser();
        return {
            milestone_name: values.milestone_name,
            description: values.description,
            start_date: values.start_date ? dayjs(values.start_date).format("YYYY-MM-DD") : null,
            end_date: values.end_date ? dayjs(values.end_date).format("YYYY-MM-DD") : null,
            due_date: values.due_date ? dayjs(values.due_date).format("YYYY-MM-DD") : null,
            assignee: values.assignee,
            department: projectDepartment !== "-" ? projectDepartment : null,
            priority: values.priority,
            progress: values.progress || 0,
            milestone_type: Array.isArray(values.milestone_type) ? values.milestone_type[0] : values.milestone_type,
            dependency: values.dependency,
            related_milestone: values.related_milestone,
            status: isDraft ? "Planned" : (values.status || "Planned"),
            estimated_effort: values.estimated_effort,
            estimated_cost: values.estimated_cost,
            deliverables: values.deliverables,
            remarks: values.remarks,
            milestone_code: milestoneCode,
            pro_id: projectId,
            pro_no: projectNo,
            is_draft: isDraft,
            added_by: user?.uid,
        };
    };

    const submitMilestone = async (values, isDraft = false) => {
        try {
            isDraft ? setSavingDraft(true) : setSubmitting(true);
            const payload = await buildPayload(values, isDraft);

            const request = isEditMode
                ? await MilestoneService.updateMilestone(payload, milestoneDbId)
                : await MilestoneService.createMilestone(payload);

            if (request?.status) {
                messageApi.success(
                    request.message || (isDraft ? "Saved as draft" : isEditMode ? "Milestone updated" : "Milestone created")
                );
                navigate(-1);
            } else {
                messageApi.error(request?.message || "Something went wrong");
            }
        } catch (error) {
            console.error("Error submitting milestone:", error);
            messageApi.error("Unexpected error occurred");
        } finally {
            setSavingDraft(false);
            setSubmitting(false);
        }
    };

    const onFinish = (values) => {
        submitMilestone(values, false);
    };

    const handleSaveDraft = () => {
        const values = form.getFieldsValue();
        submitMilestone(values, true);
    };

    const headerLabel = isEditMode ? (readOnly ? "View Milestone" : "Edit Milestone") : "Add Milestone";

    return (
        <>
            <style>{compactFormStyle}</style>
            {contextHolder}

            <Card
                loading={loadingRecord}
                title={
                    <Space align="center">
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <ArrowLeftOutlined
                                style={{ cursor: "pointer", fontSize: "20px" }}
                                onClick={() => navigate(-1)}
                            />
                            <Title level={4} style={{ margin: 0 }}>
                                <Space>
                                    {headerLabel}
                                    <Tag color="blue" style={{ fontSize: "16px", padding: "6px 16px", borderRadius: "8px", fontWeight: 600 }}>
                                        {milestoneCode}
                                    </Tag>
                                </Space>
                            </Title>
                        </div>
                    </Space>
                }
extra={
                    <Space>
                        {readOnly && canManage ? (
                            <Button
                                size="large"
                                onClick={() =>
                                    navigate("/milestones/add", {
                                        state: { ...location.state, action: "Edit", enableEdit: true, statusOnly: false },
                                    })
                                }
                                style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)", color: "#fff" }}
                            >
                                Enable Edit
                            </Button>
                        ) : readOnly && !canManage ? (
                            <Button
                                size="large"
                                onClick={() =>
                                    navigate("/milestones/add", {
                                        state: { ...location.state, action: "Edit", enableEdit: true, statusOnly: true },
                                    })
                                }
                                style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)", color: "#fff" }}
                            >
                                Update Status
                            </Button>
                        ) : (
                            <>
                                {!statusOnly && (
                                    <Button size="large" loading={savingDraft} onClick={handleSaveDraft}>
                                        Save as Draft
                                    </Button>
                                )}
                                <Button
                                    type="primary" size="large" loading={submitting} onClick={() => form.submit()}
                                    style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}
                                >
                                    {isEditMode ? "Update Milestone" : "Save Milestone"}
                                </Button>
                            </>
                        )}
                    </Space>
                }
            >
                <Row gutter={16}>
                    <Col xs={24} lg={17}>
                        <Form
                            layout="vertical"
                            form={form}
                            onFinish={onFinish}
                            disabled={readOnly}
                            initialValues={{ priority: "High", status: "Planned", progress: 0 }}
                        >
                            <Card title={<CardTitle no={1} text="Basic Information" />}>
                                <Row gutter={16}>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item label="Project">
                                            <Input size="large" value={projectName} readOnly />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="milestone_name" label="Milestone Name" rules={[{ required: true, message: "Please enter milestone name" }]}>
                                            <Input size="large" placeholder="Enter milestone name" disabled={statusOnly} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item label="Milestone Code">
                                            <Input size="large" readOnly value={milestoneCode} placeholder="Auto Generate" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="description" label="Description">
                                            <TextArea rows={5} size="large" maxLength={500} showCount placeholder="Enter description" disabled={statusOnly} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>

                            <Card title={<CardTitle no={2} text="Schedule & Duration" />}>
                                <Row gutter={16}>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="start_date" label="Start Date" rules={[{ required: true, message: "Please select start date" }]}>
                                            <DatePicker size="large" style={{ width: "100%" }} format="DD-MM-YYYY" placeholder="Select start date" disabled={statusOnly} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="end_date" label="End Date" rules={[{ required: true, message: "Please select end date" }]}>
                                            <DatePicker size="large" style={{ width: "100%" }} format="DD-MM-YYYY" placeholder="Select end date" disabled={statusOnly} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item label="Duration">
                                            <Input size="large" readOnly value={`${durationDays} Days`} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="due_date" label="Due Date">
                                            <DatePicker size="large" style={{ width: "100%" }} format="DD-MM-YYYY" placeholder="Select due date" disabled={statusOnly} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>

                            <Card title={<CardTitle no={3} text="Assignment & Progress" />}>
                                <Row gutter={16}>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="assignee" label="Assignee" rules={[{ required: true, message: "Please select assignee" }]}>
                                            <Select options={assigneeOptions} size="large" showSearch allowClear placeholder="Select assignee" disabled={statusOnly} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item label="Department / Team">
                                            <Input size="large" value={projectDepartment} readOnly />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="priority" label="Priority" rules={[{ required: true, message: "Please select priority" }]}>
                                            <Select options={priorityOptions} size="large" placeholder="Select priority" disabled={statusOnly} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="progress" label="Progress (%)">
                                            <InputNumber size="large" min={0} max={100} style={{ width: "100%" }} placeholder="0" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>

                            <Card title={<CardTitle no={4} text="Additional Information" />}>
                                <Row gutter={16}>

                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="milestone_type" label="Milestone Type">
                                            <Select
                                                options={milestoneTypeOptions}
                                                mode="tags"
                                                maxCount={1}
                                                size="large"
                                                showSearch
                                                allowClear
                                                placeholder="Select or type milestone type"
                                                disabled={statusOnly}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="dependency" label="Dependency">
                                            <Select options={dependencyOptions} size="large" showSearch allowClear placeholder="Select dependency" disabled={statusOnly} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="related_milestone" label="Related Milestone">
                                            <Select options={dependencyOptions} size="large" showSearch allowClear placeholder="Select related milestone" disabled={statusOnly} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="status" label="Status">
                                            <Select options={statusOptions} size="large" placeholder="Select status" />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="estimated_effort" label="Estimated Effort">
                                            <Select options={effortOptions} size="large" showSearch allowClear placeholder="Select effort" disabled={statusOnly} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="estimated_cost" label="Estimated Cost (₹)">
                                            <InputNumber size="large" min={0} style={{ width: "100%" }} placeholder="Enter estimated cost" disabled={statusOnly} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="deliverables" label="Deliverables">
                                            <TextArea rows={3} size="large" maxLength={300} showCount placeholder="Enter deliverables..." disabled={statusOnly} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="remarks" label="Remarks">
                                            <TextArea rows={3} size="large" maxLength={300} showCount placeholder="Enter remarks (optional)..." disabled={statusOnly} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>
                        </Form>
                    </Col>

                    {/* Right: Summary + Tips — same pattern as AddRequirement.jsx */}
                    <Col xs={24} lg={7}>
                        <Card
                            title={<span style={{ color: "rgba(14, 93, 146, 0.95)", fontWeight: 600 }}>Milestone Summary</span>}
                            size="small"
                        >
                            <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                {[
                                    ["Project", projectName],
                                    ["Milestone Name", summary.milestone_name || "-"],
                                    ["Milestone Code", milestoneCode],
                                    ["Start Date", summary.start_date ? dayjs(summary.start_date).format("DD-MM-YYYY") : "-"],
                                    ["End Date", summary.end_date ? dayjs(summary.end_date).format("DD-MM-YYYY") : "-"],
                                    ["Duration", `${durationDays} Days`],
                                    ["Assignee", summary.assignee || "-"],
                                    ["Priority", summary.priority || "-"],
                                    ["Status", summary.status || "Planned"],
                                ].map(([label, value]) => (
                                    <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                                        <Text type="secondary">{label}</Text>
                                        <Text strong>{value}</Text>
                                    </div>
                                ))}
                            </Space>
                        </Card>

                        <Card
                            title={<span style={{ color: "rgba(14, 93, 146, 0.95)", fontWeight: 600 }}>Helpful Tips</span>}
                            size="small"
                        >
                            <ul style={{ paddingLeft: 18, margin: 0, fontSize: 13, color: "#555", lineHeight: 1.9 }}>
                                <li>Break the project into clear milestones.</li>
                                <li>Each milestone should have a measurable deliverable.</li>
                                <li>Set realistic start and end dates.</li>
                                <li>Assign the right person or team.</li>
                                <li>Update progress regularly.</li>
                            </ul>
                        </Card>

                        <Card
                            title={<span style={{ color: "rgba(14, 93, 146, 0.95)", fontWeight: 600 }}>Quick Links</span>}
                            size="small"
                        >
                            <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                {["View Project", "Milestone List", "Project Timeline", "Gantt Chart"].map((label) => (
                                    <div
                                        key={label}
                                        onClick={() => navigate(-1)}
                                        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", fontSize: 13, color: "rgba(14, 93, 146, 0.95)" }}
                                    >
                                        <span>{label}</span>
                                        <span>›</span>
                                    </div>
                                ))}
                            </Space>
                        </Card>
                    </Col>
                </Row>

                {/* Footer buttons */}
                {!readOnly && (
                    <div style={{ textAlign: "right", marginTop: 16 }}>
                        <Button size="large" style={{ marginRight: 8 }} onClick={() => form.resetFields()}>
                            Reset
                        </Button>
                        <Button
                            type="primary" size="large" loading={submitting} onClick={() => form.submit()}
                            style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}
                        >
                            {isEditMode ? "Update Milestone" : "Save Milestone"}
                        </Button>
                    </div>
                )}
            </Card>
        </>
    );
};

export default AddMilestone;